import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CodeBracketIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LinkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { LayoutGrid, Globe, Database, Layers, Brain } from 'lucide-react';

import projects from '../Data/projects.json';
import { FolderKanban } from 'lucide-react';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeProject, setActiveProject] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 6;

  const filters = [
    { id: 'All', label: 'All', icon: LayoutGrid },
    { id: 'Web', label: 'Web', icon: Globe },
    { id: 'Big Data', label: 'Big Data', icon: Database },
    { id: 'Full Stack', label: 'Full Stack', icon: Layers },
    { id: 'AI/ML', label: 'AI/ML', icon: Brain },
  ];

  const filteredProjects = activeFilter === 'All' ? projects : projects.filter(p => p.category === activeFilter);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

  const openProjectModal = (project) => {
    setActiveProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectModal = () => {
    setActiveProject(null);
    document.body.style.overflow = 'auto';
  };

  const navigateProject = useCallback((direction) => {
    if (!activeProject) return;
    const currentIndex = projects.findIndex(p => p.id === activeProject.id);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? projects.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === projects.length - 1 ? 0 : currentIndex + 1;
    }
    setActiveProject(projects[newIndex]);
  }, [activeProject]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!activeProject) return;
      if (e.key === 'Escape') closeProjectModal();
      else if (e.key === 'ArrowLeft') navigateProject('prev');
      else if (e.key === 'ArrowRight') navigateProject('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeProject, navigateProject]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  const categoryConfig = {
    'Big Data': { icon: <Database className="w-4 h-4" /> },
    'AI/ML': { icon: <Brain className="w-4 h-4" /> },
    'Web': { icon: <Globe className="w-4 h-4" /> },
    'Full Stack': { icon: <Layers className="w-4 h-4" /> }
  };

  return (
    <section id="projects" className="py-20 bg-white dark:bg-black">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-3xl text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
            <FolderKanban className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
            Selected Works
          </h2>
          <div className="w-40 h-px bg-gray-500 dark:bg-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 max-w-5xl mx-auto text-base font-light">
            Bridging the gap between complex data and intuitive user experiences through clean, scalable architecture.
          </p>
        </motion.div>

        {/* Filter tabs – underline style */}
        <div className="flex justify-center mb-10 w-full">
  {/* 1. overflow-x-auto allows swiping on mobile
      2. scrollbar-hide keeps it clean (requires custom CSS or utility)
      3. max-w-full prevents the container from breaking the page width
  */}
  <div className="flex items-center gap-4 md:gap-8 overflow-x-auto no-scrollbar pb-2 px-4 max-w-full sm:justify-center">
    {filters.map((filter) => {
      const Icon = filter.icon;
      return (
        <button
          key={filter.id}
          className={`pb-2 text-sm font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap shrink-0 ${
            activeFilter === filter.id
              ? 'text-gray-900 dark:text-white border-indigo-500'
              : 'text-gray-400 dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveFilter(filter.id)}
        >
          <Icon className={`w-4 h-4 ${activeFilter === filter.id ? 'text-indigo-500' : ''}`} />
          {filter.label}
        </button>
      );
    })}
  </div>
</div>

        {filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <FolderKanban className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium mb-1">No projects found</h3>
            <p className="text-sm">There are no projects in this category yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="group cursor-pointer"
                  onClick={() => openProjectModal(project)}
                >
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 h-full flex flex-col hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
                    {/* Category badge */}
                    <div className="flex justify-between items-start mb-3">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        {categoryConfig[project.category]?.icon}
                        {project.category}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {project.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                      {project.description}
                    </p>

                    {/* Tech tags – simple dot-separated list */}
                    <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mt-auto">
                      {project.technologies.slice(0, 4).map((tech, i) => (
                        <span key={i} className="flex items-center">
                          {tech}
                          {i < Math.min(project.technologies.length, 4) - 1 && (
                            <span className="text-gray-300 dark:text-gray-600 mx-1">•</span>
                          )}
                        </span>
                      ))}
                      {project.technologies.length > 4 && (
                        <span className="text-gray-400">+{project.technologies.length - 4}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-10 gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border ${
                    currentPage === 1
                      ? 'border-gray-200 dark:border-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-500 dark:hover:border-indigo-400'
                  }`}
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-sm ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border ${
                    currentPage === totalPages
                      ? 'border-gray-200 dark:border-gray-800 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-500 dark:hover:border-indigo-400'
                  }`}
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal – no images, cleaner layout */}
      <AnimatePresence>
        {activeProject && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/50 dark:bg-black/70"
              onClick={closeProjectModal}
            />
            <motion.div
              className="relative bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-gray-800"
              initial={{ opacity: 0, scale: 0.98, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Close button */}
              <button
                onClick={closeProjectModal}
                className="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white p-1 rounded-full"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* Project details – no image area */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h2 className="text-xl font-medium text-gray-900 dark:text-white">
                    {activeProject.title}
                  </h2>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-full">
                    {categoryConfig[activeProject.category]?.icon}
                    {activeProject.category}
                  </span>
                </div>

                {activeProject.subtitle && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{activeProject.subtitle}</p>
                )}

                <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 leading-relaxed">
                  {activeProject.description}
                </p>

                {activeProject.features && activeProject.features.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Features
                    </h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {activeProject.features.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-4">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Technologies
                  </h4>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
                    {activeProject.technologies.map((tech, i) => (
                      <span key={i} className="flex items-center">
                        {tech}
                        {i < activeProject.technologies.length - 1 && (
                          <span className="text-gray-300 dark:text-gray-600 mx-2">•</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>

                {activeProject.links && (activeProject.links.demo || activeProject.links.code || activeProject.links.docs) && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    {activeProject.links.demo && (
                      <a
                        href={activeProject.links.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                      >
                        <LinkIcon className="w-3.5 h-3.5" />
                        Live Demo
                      </a>
                    )}
                    {activeProject.links.code && (
                      <a
                        href={activeProject.links.code}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                      >
                        <CodeBracketIcon className="w-3.5 h-3.5" />
                        Code
                      </a>
                    )}
                    {activeProject.links.docs && (
                      <a
                        href={activeProject.links.docs}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                      >
                        <DocumentTextIcon className="w-3.5 h-3.5" />
                        Docs
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;